import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useLocale } from "@/lib/i18n/useLocale";
import { entryByKey, HTML_LANG, SITE_ORIGIN } from "@/lib/i18n/registry";
import { Blocks, schemaFromBlocks } from "../blocks/Blocks";
import type { Block, LocaleMeta } from "../blocks/types";

/**
 * Оболочка блочной страницы (BTQ-47, Этап 1).
 *
 * Даёт странице то же обрамление, что и всем остальным — Layout с общими
 * шапкой и футером, — и собирает голову из той же схемы, из которой рендерится
 * текст. Разметка выводится из блоков (`schemaFromBlocks`), поэтому она не
 * может разойтись с видимым текстом: в v1 они велись раздельно, и на EB-стороне
 * граф ссылался на узел Organization, которого на странице не было.
 */
export default function BlockPage({
  routeKey,
  meta,
  blocks,
  breadcrumb,
}: {
  routeKey: string;
  meta: LocaleMeta;
  blocks: Block[];
  breadcrumb?: string;
}) {
  const locale = useLocale();
  const entry = entryByKey(routeKey);
  const path = locale === "pt" ? entry?.pt : entry?.en;
  const canonical = `${SITE_ORIGIN}${path ?? "/"}`;

  // Полноэкранным становится только герой с фотографией и только если он первый.
  const first = blocks[0];
  const hero = first && first.type === "hero" && first.image ? first : null;
  const rest = hero ? blocks.slice(1) : blocks;

  return (
    <Layout>
      <SEOHead
        title={meta.title}
        description={meta.description}
        canonical={canonical}
        lang={HTML_LANG[locale]}
        schemaJson={schemaFromBlocks(blocks, `${SITE_ORIGIN}/#org`)}
      />
      {/*
        Герой с фотографией обязан выйти из колонки текста: на английских
        страницах он во всю ширину экрана, и португальский двойник в рамке
        max-w-4xl читался бы как другая вёрстка. Остальные блоки остаются в
        типографской мере — длинная строка текста нечитаема.
      */}
      {hero ? (
        <>
          <Blocks blocks={[hero]} />
          <div className="pt-12 pb-16 md:pb-24" data-header-theme="light">
            <div className="luxury-container max-w-4xl mx-auto">
              {breadcrumb && <Breadcrumbs items={[{ label: breadcrumb }]} />}
              <Blocks blocks={rest} />
            </div>
          </div>
        </>
      ) : (
        <div className="pt-32 pb-16 md:pb-24" data-header-theme="light">
          <div className="luxury-container max-w-4xl mx-auto">
            {breadcrumb && <Breadcrumbs items={[{ label: breadcrumb }]} />}
            <Blocks blocks={blocks} />
          </div>
        </div>
      )}
    </Layout>
  );
}
